/** Income-goal API — CFM juice-income vs weekly/monthly/quarterly/annual goals. */
import { apiGet, apiPost, apiPut } from './client'

export type CycleStatus = 'hit' | 'on_track' | 'behind' | 'no_goal'

export interface IncomeHistoryPoint {
  label: string; actual: number; goal: number; hit: boolean; current: boolean
  realized?: number; unrealized?: number
}
export interface IncomeCycle {
  label: string; start: string; end: string
  goal: number; current: number; pct: number | null
  /** Juice booked on closed quantity (banked in the close week). */
  realized: number
  /** Entry juice still open (projected in the expiry week). */
  unrealized: number
  status: CycleStatus
  ytd_actual: number; ytd_goal: number; ytd_delta: number
  needed_per_remaining: number; remaining_cycles: number
  history: IncomeHistoryPoint[]
}
export interface UpcomingWeek {
  label: string; start: string; end: string; week_ending: string
  goal: number; current: number; pct: number | null
  realized: number; unrealized: number
  hit: boolean; trade_count: number
}
export interface ExcludedTrade {
  event_id: number
  underlying: string
  strike: number | null
  right: string | null
  expiry: string
  status: string
  open_qty: number
  close_qty: number
  open_price: number | null
  close_price: number | null
  first_fill_at: string | null
}
export interface IncomeSummary {
  as_of: string
  scope: string | string[]
  ytd_income: number; annual_goal: number; ytd_gap: number; excluded_events: number
  // Detail of each excluded trade (missing captured entry underlying) so the
  // dashboard can name them and offer a refetch — not just a bare count.
  excluded?: ExcludedTrade[]
  cycles: { weekly: IncomeCycle; monthly: IncomeCycle; quarterly: IncomeCycle; annual: IncomeCycle }
  upcoming_weeks?: UpcomingWeek[]
  // Past 4 weeks (before the current week) — same shape as upcoming_weeks,
  // rendered as an identical tile grid above the Income Goals card.
  past_weeks?: UpcomingWeek[]
}
export interface IncomeGoals {
  stored: Partial<Record<'weekly' | 'monthly' | 'quarterly' | 'annual', number>>
  resolved: { weekly: number; monthly: number; quarterly: number; annual: number }
}

export const getIncomeDashboard = (scope?: string) =>
  apiGet<IncomeSummary>('/dashboard/income-goals', { params: scope ? { scope } : undefined })
export const getIncomeGoals = () => apiGet<IncomeGoals>('/settings/income-goals')
export const updateIncomeGoals = (body: IncomeGoals['stored']) =>
  apiPut<IncomeGoals>('/settings/income-goals', body)

export interface IncomeTrade {
  underlying: string; strike: number | null; right: string | null; expiry: string
  /** Strict /GC form ('/ES' for futures) — preferred for display. */
  underlying_display?: string
  status: string; open_qty: number; close_qty: number
  open_price: number | null; close_price: number | null; income: number
  /** Which income component this row is (an event can appear in two cycles). */
  part?: 'realized' | 'unrealized'
}
export interface IncomeCycleRow {
  label: string; start: string; end: string
  actual: number; goal: number; delta: number; pct: number | null
  realized: number; unrealized: number
  hit: boolean; current: boolean; trade_count: number; trades: IncomeTrade[]
  // Portfolio side (null before NLV history / for future cycles):
  portfolio_value: number | null      // NLV at cycle end
  account_change_raw: number | null   // ΔNLV as the statement shows it
  net_deposits: number                // deposits − withdrawals; known even when ΔNLV isn't
  account_delta: number | null        // account_change_raw − net_deposits = performance
  ex_juice_delta: number | null       // account_delta − juice = the asset/capital side
  bleeding: boolean                   // collected juice, account still went backwards
}
export interface IncomeCyclesResponse {
  granularity: string; scope: string | string[]; goal: number; excluded_events: number
  portfolio_from: string | null    // earliest date Account Δ is available (NLV history start)
  cycles: IncomeCycleRow[]
}
export const getIncomeCycles = (granularity: string, scope?: string, limit = 26) =>
  apiGet<IncomeCyclesResponse>('/dashboard/income-goals/cycles',
    { params: { granularity, limit, ...(scope ? { scope } : {}) } })

/** Backfill one execution event's missing entry underlying from IB historical
 *  data (at its first-fill instant). Repairs a trade excluded from income
 *  because its open fill was captured without an underlying price. */
export interface RefetchUnderlyingResult {
  ok?: boolean
  error?: string
  event_id?: number
  underlying?: string
  fill_at_utc?: string
  entry_underlying?: number
}
export const refetchEventUnderlying = (eventId: number, alias = 'personal') =>
  apiPost<RefetchUnderlyingResult>(
    `/ingest/execution-events/${eventId}/refetch-underlying`,
    undefined,
    { params: { alias } },
  )
